/**
 * A ticket's files (TMMPDM-0110; TMMAPI-0181 U7 item 46): name, type and size, each with Download.
 * A file is fetched as a blob through the axios factory (bearer-protected; never a bare link to the
 * API) and saved under its name. Nothing a member sent is ever rendered: no image, no document or
 * HTML preview, no frame. The blob is re-typed as an opaque download before it is saved, so the
 * browser never opens it in place. With no metadata from the server there is no list at all.
 */
import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy } from '@/api/errors';
import type { TicketAttachmentView } from '@/api/tmm/types';
import { formatFileSize } from '@/format';
import { saveBlob } from '@/routes/payouts/downloadCsv';
import { downloadName } from './tickets';

interface TicketAttachmentsProps {
  /** The ticket's bare number: the download path addresses it, never "SC-1001". */
  number: number;
  attachments: readonly TicketAttachmentView[];
  /** The list's accessible name ("Attachments", or "Attached files" on an update). */
  label: string;
}

export function TicketAttachments({ number, attachments, label }: TicketAttachmentsProps) {
  if (attachments.length === 0) return null;
  return (
    <Stack component="ul" aria-label={label} spacing={1} sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {attachments.map((file) => (
        <AttachmentRow key={String(file.attachmentId)} number={number} file={file} />
      ))}
    </Stack>
  );
}

function AttachmentRow({ number, file }: { number: number; file: TicketAttachmentView }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const blob = await adminApi.downloadTicketAttachment(number, file.attachmentId);
      // An opaque type: the browser saves it and never renders a member's file in place.
      saveBlob(new Blob([blob], { type: 'application/octet-stream' }), downloadName(file.name));
    } catch (failure: unknown) {
      setError(
        failure instanceof ApiError && failure.status === 404
          ? t('tickets.attachments.gone', 'This file is no longer available.')
          : errorCopy(failure, t, t('tickets.attachments.error', "Couldn't download this file.")),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box component="li">
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <InsertDriveFileOutlinedIcon fontSize="small" color="action" aria-hidden="true" />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {file.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {t('tickets.attachments.meta', '{{type}} · {{size}}', {
              type: file.contentType,
              size: formatFileSize(file.sizeBytes, i18n.language),
            })}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() => void download()}
          aria-label={t('tickets.attachments.downloadAria', 'Download {{name}}', {
            name: file.name,
          })}
        >
          {t('tickets.attachments.download', 'Download')}
        </Button>
      </Stack>
      {error ? (
        <Typography role="alert" variant="body2" color="error" sx={{ mt: 0.5, ml: 4.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}
